import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { leadFlowLog } from '../utils/leadFlowLogger';

export const CALL_STATUS = {
  IDLE: 'IDLE',
  CALLING: 'CALLING',
  ENDED_PENDING_DISPOSITION: 'ENDED_PENDING_DISPOSITION',
};

export const useCallStore = create(
  persist(
    (set, get) => ({
      currentLead: null,
      viewingLead: null,
      callStatus: CALL_STATUS.IDLE,
      dispositionRequired: false,
      isPolling: false,
      webphoneUrl: null,
      sessionRestored: false,

      setViewingLead: (lead) => set({ viewingLead: lead }),

      setCurrentLead: (lead) => {
        leadFlowLog.storeAction('setCurrentLead', {
          leadID: lead?.leadID ?? lead?.id,
          leadName: lead?.leadName,
        });
        set({ 
          currentLead: lead,
          dispositionRequired: false,
        });
      },

      clearCurrentLead: () => {
        set({ 
          currentLead: null,
          dispositionRequired: false,
        });
      },

      setWebphoneUrl: (url) => {
        set({ webphoneUrl: url || null });
      },

      /**
       * True only while THIS page instance is the one that started the call.
       * Excluded from `partialize` below on purpose: after a reload it comes back
       * false while callStatus is still CALLING, which is the exact marker for a
       * call whose SIP session died with the previous page. See utils/zombieCall.
       */
      ownedBySession: false,

      startCall: (lead, webphoneUrl) => {
        leadFlowLog.storeAction('startCall', { leadID: lead?.leadID ?? lead?.id });
        set({
          currentLead: lead,
          callStatus: CALL_STATUS.CALLING,
          dispositionRequired: false,
          ownedBySession: true,
          webphoneUrl: webphoneUrl || get().webphoneUrl || null,
        });
      },

      endCall: () => {
        leadFlowLog.storeAction('endCall', {});
        set({
          callStatus: CALL_STATUS.ENDED_PENDING_DISPOSITION,
          dispositionRequired: true,
          ownedBySession: false,
        });
      },

      completeDisposition: () => {
        leadFlowLog.storeAction('completeDisposition', { currentLeadCleared: true });
        set({
          callStatus: CALL_STATUS.IDLE,
          dispositionRequired: false,
          currentLead: null,
        });
      },

      setPolling: (isPolling) => {
        set({ isPolling });
      },

      setSessionRestored: (restored) => {
        set({ sessionRestored: restored });
      },

      reset: () => {
        set({
          currentLead: null,
          callStatus: CALL_STATUS.IDLE,
          dispositionRequired: false,
          isPolling: false,
          webphoneUrl: null,
          sessionRestored: false,
        });
      },
    }),
    {
      name: 'nicedial-call-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentLead: state.currentLead,
        callStatus: state.callStatus,
        webphoneUrl: state.webphoneUrl,
        dispositionRequired: state.dispositionRequired,
      }),
    }
  )
);
